import type { PublicationContext } from '~/types';
import { useCommonStore } from '~/store/common';
import { useProfileLoginStore } from '~/store/profileLogin';

export type AudioCtaVariant = 'login' | 'subscribe' | 'none';

/* Appends query params while keeping relative URLs relative. */
export const appendAudioQueryParams = (url: string, params: Record<string, string>): string => {
  const isAbsolute = /^https?:\/\//i.test(url);
  const parsed = new URL(url, 'https://local.invalid');

  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      parsed.searchParams.set(key, value);
    }
  });

  if (isAbsolute) {
    return parsed.toString();
  }

  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
};

/* Which call to action the subscription gate should offer. */
export const resolveAudioCtaVariant = (isLoggedIn: boolean, isSubscriber: boolean): AudioCtaVariant => {
  if (!isLoggedIn) {
    return 'login';
  }

  return isSubscriber ? 'none' : 'subscribe';
};

/*
 * Audio is gated behind an active subscription. `source` identifies the
 * surface for tracking and for the login / subscription funnel
 * (e.g. 'audio_playlist_article', 'audio_playlist_front').
 *
 * Note: the login state is populated client-side only, on app:beforeMount
 * (plugins/userprofile.client.ts), so callers must react to `canListen`
 * changing rather than reading it once during setup.
 */
export default function useAudioAccess (source: string) {
  const config = useRuntimeConfig();
  const publicConfig = config.public as unknown as PublicationContext;
  const route = useRoute();
  const commonStore = useCommonStore();
  const profileLoginStore = useProfileLoginStore();
  const mobileApps = useMobileAppsDetector();

  const isLoggedIn = computed(() => profileLoginStore.loginStatus.isLoggedIn);
  const isSubscriber = computed(() => profileLoginStore.loginStatus.isSubscriber || profileLoginStore.loginStatus.isTotalSubscriber);
  const canListen = computed(() => isLoggedIn.value && isSubscriber.value);
  const ctaVariant = computed(() => resolveAudioCtaVariant(isLoggedIn.value, isSubscriber.value));
  const needsSubscription = computed(() => ctaVariant.value === 'subscribe');
  const target = computed(() => route.fullPath || '/');

  const subscriptionHref = computed(() => {
    const value = (config.public as Record<string, unknown>).subscriptionLink;
    const baseLink = typeof value === 'string' && value.length > 0 ? value : '/subscription/';

    return appendAudioQueryParams(baseLink, { source, target: target.value });
  });

  const loginHref = computed(() => {
    const isDnMobileApp = import.meta.client &&
      publicConfig.name?.toLowerCase() === 'dn' &&
      (commonStore.isAppView || mobileApps.isAndroid() || mobileApps.isIos());

    if (isDnMobileApp) {
      return '/auth/user/login';
    }

    return appendAudioQueryParams('/login', { source, target: target.value });
  });

  const loginTooltipCtaHref = computed(() => (needsSubscription.value ? subscriptionHref.value : loginHref.value));

  const ctaTrackEvent = computed(() => ({
    eventName: 'Audio',
    component: {
      zone: 'audio-player',
      element: 'dn-audio-login-tooltip-cta',
      element_id: needsSubscription.value ? 'audio-subscribe' : 'audio-login'
    },
    attributes: { link_url: loginTooltipCtaHref.value }
  }));

  return {
    isLoggedIn,
    isSubscriber,
    canListen,
    needsSubscription,
    loginHref,
    subscriptionHref,
    loginTooltipCtaHref,
    ctaTrackEvent,
    trackingTarget: target
  };
}
